'use client';

import { useEffect, useRef, useState } from 'react';

interface ClampedTextProps {
  text: string;
  className?: string;
  /** Number of lines to clamp to before overflow is measured (both the
   *  header tagline and the Details Card description clamp to 3 lines). */
  lines?: number;
}

/**
 * "…Show more"/"Show less" toggle, shown ONLY when the text actually
 * overflows its clamped height — matches the reference's
 * `handleDescriptionToggle()` (measures `scrollHeight` against
 * `lineHeight * 3`, not a fixed character count) rather than hiding/showing
 * the toggle based on string length. Used by both `ShowDetailHeader`'s
 * tagline and `ShowDetailsCard`'s description (brd-frontend.md §2, §4).
 */
export function ClampedText({ text, className = '', lines = 3 }: ClampedTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure against the clamped (collapsed) state only.
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <p
      ref={ref}
      className={`${className} ${!expanded ? `shows-detail-clamp-${lines}` : ''}`}
    >
      {text}
      {overflowing && (
        <button type="button" className="shows-detail-toggle-link" onClick={() => setExpanded((v) => !v)}>
          {' '}
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </p>
  );
}
